import { Injectable, computed, signal } from '@angular/core';
import { ApiMeDto } from '@vasta/api-vasta';
import { IMPERSONATION_QUERY_PARAM } from '@vasta/frontend-shared-core-authentication';

const STORAGE_KEY = 'vasta_impersonation_target';

/**
 * Which user this tab is viewing the application as, or nothing.
 *
 * Per tab, in `sessionStorage`, deliberately: the hand-off belongs to the tab
 * the admin opened, and closing it is how the impersonation ends. The API is
 * what decides whether it is honoured — this only carries the request.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private readonly _targetId = signal<number | null>(this.readFromStorage());

  readonly targetId = this._targetId.asReadonly();
  readonly active = computed(() => this._targetId() !== null);

  /**
   * Takes the hand-off from the URL and strips it, so a reload, a bookmark or
   * a shared link cannot re-arm it. Called before the login flow: the tab can
   * bounce through Zitadel and come back to `/auth/callback` without the
   * parameter.
   */
  captureFromUrl(): void {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(IMPERSONATION_QUERY_PARAM);
    if (raw === null) return;

    url.searchParams.delete(IMPERSONATION_QUERY_PARAM);
    // Through `replaceState` rather than a router navigation: this runs during
    // bootstrap, and a navigation here would race the login flow. The router's
    // own state object is passed back untouched.
    window.history.replaceState(window.history.state, '', url.toString());

    // The first hand-off wins: a tab's identity is set once, so editing the
    // URL afterwards cannot point it at somebody else.
    if (this.active()) return;

    const targetId = Number(raw);
    if (!Number.isInteger(targetId) || targetId <= 0) return;

    this.write(targetId);
  }

  /**
   * Lets `/users/me` settle the question. The API answers as the caller when it
   * did not honour the hand-off — no permission, target gone — and the tab must
   * stop claiming otherwise. A missing answer (the call failed) decides nothing.
   */
  reconcile(user: ApiMeDto | null): void {
    if (this.active() && user && !user.impersonatedBy) {
      this.stop();
    }
  }

  /** Drops the hand-off, keeping the tab where it is. */
  stop(): void {
    this._targetId.set(null);
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // sessionStorage unavailable (e.g. private browsing) — nothing stored
    }
  }

  /**
   * Drops the hand-off and rebuilds the tab around the caller. Current user,
   * tenants and guards all have to be asked for again, and a reload is the one
   * path that leaves nothing of the target behind.
   */
  exit(): void {
    this.stop();
    window.location.assign('/');
  }

  private readFromStorage(): number | null {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const targetId = Number(raw);
      return Number.isInteger(targetId) && targetId > 0 ? targetId : null;
    } catch {
      return null;
    }
  }

  private write(targetId: number): void {
    this._targetId.set(targetId);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, String(targetId));
    } catch {
      // sessionStorage unavailable — the tab keeps the hand-off in memory only
    }
  }
}
